import { debugService } from "@/features/debug";
import {
  isKnownDirection,
  readDirection,
} from "@/features/positions/utils/direction-display";

const CONTRACT_MULTIPLIERS: Record<string, number> = {
  ES: 50,
  NQ: 20,
  YM: 5,
  RTY: 50,
  GC: 100,
  CL: 1000,
  MES: 5,
  MNQ: 2,
  // SIG-350: the remaining CME micros — absent entries meant their roots
  // never normalized (MGCQ6 → "MGCQ") and their P&L used no multiplier.
  MYM: 0.5,
  MGC: 10,
  M2K: 5,
  MCL: 100,
};

export const normalizeFuturesRoot = (value: unknown): string | null => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return null;

  const roots = Object.keys(CONTRACT_MULTIPLIERS).sort((a, b) => b.length - a.length);
  for (const root of roots) {
    if (raw.startsWith(root)) {
      return root;
    }
  }

  return raw.replace(/[^A-Z]/g, "") || null;
};

export const quoteRootFor = normalizeFuturesRoot;

/**
 * Helper function to find the relevant quote for a specific contract
 * @param quoteData Array of quote data objects
 * @param contractId The contract ID to look for
 * @returns The quote object if found, otherwise null
 */
const findQuoteForContract = (
  quoteData: any[],
  contractId: string | null,
  symbolCandidates: string[] = [],
): any => {
  // Search for the contract in all quote data items
  for (const item of quoteData) {
    if (contractId && item[contractId]) {
      return item[contractId];
    }

    for (const symbol of symbolCandidates) {
      if (item[symbol]) {
        return item[symbol];
      }
      if (item.symbol === symbol || item.contract_symbol === symbol || item.instrument === symbol) {
        return item;
      }
    }
  }

  // WS-B2 fallback: live Databento quotes arrive keyed by futures ROOT ("MNQ")
  // while positions carry the dated contract ("MNQU6") — exact match misses.
  // Root-normalized matching marks the position off its root's live quote
  // (front-month only in practice; a root maps to one live subscription).
  const candidateRoots = symbolCandidates
    .map((s) => normalizeFuturesRoot(s))
    .filter(Boolean);
  for (const item of quoteData) {
    const itemRoot = normalizeFuturesRoot(item?.symbol);
    if (itemRoot && candidateRoots.includes(itemRoot)) {
      return item;
    }
  }
  return null;
};

/**
 * Resolve the contract multiplier (point value) for a position.
 * Prefers broker-provided multipliers, falls back to the futures root map.
 * Returns null when unknown — callers must treat that honestly (no guess).
 */
export const getContractMultiplier = (position: {
  contract_multiplier?: unknown;
  instrument?: unknown;
  contract_symbol?: unknown;
  broker_metadata?: {
    contract_multiplier?: unknown;
    symbol?: unknown;
    contract_symbol?: unknown;
  } | null;
}): number | null => {
  const explicit = Number(
    position?.contract_multiplier ||
      position?.broker_metadata?.contract_multiplier,
  );
  if (explicit) return explicit;

  const root = [
    position?.contract_symbol,
    position?.instrument,
    position?.broker_metadata?.symbol,
    position?.broker_metadata?.contract_symbol,
  ]
    .map((value) => normalizeFuturesRoot(value))
    .find((value) => value && CONTRACT_MULTIPLIERS[value]);

  return root ? CONTRACT_MULTIPLIERS[root] : null;
};

/**
 * Calculates the Profit and Loss (PnL) for a trading position based on current market data
 * @param position The position object containing trade details
 * @param quoteData The market data quote update containing latest prices
 * @returns The calculated PnL fixed to 2 decimal places, or null when the
 *          inputs cannot price the position (missing quote/price/multiplier).
 *          A miss used to return 0, which callers wrote into the cache as a
 *          KNOWN-flat "$0.00" — a wrong number, not a missing one. Callers
 *          must skip the write on null and keep the previous mark.
 */
export const calculatePositionPnL = (position: any, quoteData: any): number | null => {

  /* PNL CALCULATION FORMULA

    TICK PRICE * QUANTITY/VOLUME (CONTRACT SIZE) = TICK VALUE

    TICK_VALUE * TICK_DELTA = PNL

  */

  // Validation - early return if missing required data
  if (!position || !quoteData?.data || !Array.isArray(quoteData.data) || quoteData.data.length === 0) {
    return null;
  }

  // Position details
  const contractId = position.broker_contract?.contract_id?.toString() || null;
  const direction = readDirection(position.direction ?? position.side);
  // Unknown side must not borrow the short formula — that inverted live
  // shorts that arrived before direction landed (R3 tape t=69 / t=83).
  if (!isKnownDirection(direction)) {
    return null;
  }
  const openPrice = parseFloat(position.open_price);
  const volume = parseFloat(position.volume);
  const symbolCandidates = [
    position.contract_symbol,
    position.instrument,
    position.broker_contract?.contract_symbol,
    position.broker_metadata?.symbol,
    position.broker_metadata?.contract_symbol,
  ]
    .map((value: unknown) => String(value || "").trim().toUpperCase())
    .filter(Boolean);

  // More validation
  if (isNaN(openPrice) || isNaN(volume) /* || volume === 0 */) {
    //console.log('[WebSocketDebug] No contract ID or invalid data found for position:', position.id);
    debugService.log('warning', 'Open price or volume is NaN for position', { positionId: position.id, openPrice, volume })
    return null;
  }

  // Configuration - to be replaced with dynamic values later
  const fallbackRoot = symbolCandidates
    .map((value: string) => normalizeFuturesRoot(value))
    .find(Boolean);
  const contractMultiplier =
    Number(position.contract_multiplier || position.broker_metadata?.contract_multiplier) ||
    (fallbackRoot ? CONTRACT_MULTIPLIERS[fallbackRoot] : null);
  const commission = 0;         // Placeholder for commission calculation

  if (!contractMultiplier) {
    debugService.log('warning', 'Contract multiplier not found for position', { positionId: position.id })
    return null;
  }

  // Find the quote for this contract
  const quote = findQuoteForContract(quoteData.data, contractId, symbolCandidates);
  if (!quote) {
    debugService.log('warning', 'No quote found for position', { positionId: position.id })
    return null;
  }

  // Price resolution mirrors getAppropriatePrice: MID of the book when both
  // sides are present (the backend's position_marks mid contract — one-sided
  // marking left P&L a spread-width off the broker permanently, P2 stage 1
  // 2026-08-12), then direction-side candidates as fallback through the same
  // aliases (ask/price/last) a nested un-normalised payload can carry.
  // Bid/offer-only reads returned 0 for trades-only schemas, which stamped a
  // live position flat.
  const inBand = (v: unknown): number | null => {
    const parsed = typeof v === 'string' ? Number(v.trim() || NaN) : Number(v);
    return v != null && Number.isFinite(parsed) && parsed > 0 && parsed <= 10_000_000 ? parsed : null;
  };
  const midBid = inBand(quote.bid);
  const midOffer = inBand(quote.offer ?? quote.ask);
  const priceCandidates =
    midBid !== null && midOffer !== null
      ? [(midBid + midOffer) / 2]
      : direction === "long"
        ? [quote.bid, quote.price, quote.last]
        : [quote.offer, quote.ask, quote.price, quote.last];
  let currentPrice: number | null = null;
  for (const candidate of priceCandidates) {
    const parsed = typeof candidate === 'string' ? Number(candidate.trim() || NaN) : Number(candidate);
    // Bounded, not just finite: feed sentinels ("no value" = int64-max nanos,
    // 9.22e9) are finite and once priced a 3-lot MNQ at +$55.3B (2026-08-12).
    // A tradable price is positive and nowhere near eight figures.
    if (candidate != null && Number.isFinite(parsed) && parsed > 0 && parsed <= 10_000_000) {
      currentPrice = parsed;
      break;
    }
  }
  if (currentPrice === null) {
    debugService.log('warning', 'No current price found for position', { positionId: position.id })
    return null;
  }

  const priceDifference =
    direction === "long"
      ? currentPrice - openPrice
      : openPrice - currentPrice;

  // Debugging log from original code
  /* if (position.id == 16244) {
     console.log(
       `Calculate PnL for position ${position.id}`,
       `Open Price: ${openPrice}`,
       `Current Price: ${currentPrice}`,
       `Direction: ${direction}`,
       priceDifference,
       volume,
       contractMultiplier,
     )
  } */

  const rawPnL = (priceDifference * volume) * contractMultiplier;
  const netPnL = rawPnL - commission;

  // Return with 2 decimal places
  return parseFloat(netPnL.toFixed(2));
};
